/**
 * KnowledgeOverlay3D — world-space overlays that follow exploded parts.
 *
 *   <TraceLines paths partsApiRef animate />   connection paths between part
 *        centres; dashed, type-coloured, dash flow while visible; label on hover
 *   <HopTrail hops partsApiRef />              numbered hop badges + a thin
 *        polyline joining the hops in order
 *
 * Rendered inside the Canvas but OUTSIDE the scene's scaled group: positions
 * come from partsApiRef (world space). All per-frame work is ref mutation in
 * useFrame; geometry is rewritten only when an endpoint actually moved, and the
 * loop is invalidated only while something moves (or the dash flow runs).
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import { connectionType } from '../diagnosticPalette.js';
import './KnowledgeOverlay3D.css';

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const EPS = 1e-5;
const SEED_POINTS = [[0, 0, 0], [0, 0.001, 0]];

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Write xyz into `cache` at `offset`; true when it differed. */
function stash(cache, offset, v) {
  if (Math.abs(cache[offset] - v.x) < EPS && Math.abs(cache[offset + 1] - v.y) < EPS
    && Math.abs(cache[offset + 2] - v.z) < EPS) return false;
  cache[offset] = v.x; cache[offset + 1] = v.y; cache[offset + 2] = v.z;
  return true;
}

function PathLine({ path, partsApiRef, animate, hovered, onHover }) {
  const lineRef = useRef(null);
  const labelRef = useRef(null);
  const cache = useRef(new Float64Array(6).fill(Number.NaN));
  const invalidate = useThree((s) => s.invalidate);
  const spec = connectionType(path.type);

  useFrame((_s, delta) => {
    const line = lineRef.current;
    const api = partsApiRef.current;
    if (!line) return;
    if (!api || !api.centreOf(path.from, _a) || !api.centreOf(path.to, _b)) {
      line.visible = false;
      return;
    }
    line.visible = true;
    const movedA = stash(cache.current, 0, _a);
    const movedB = stash(cache.current, 3, _b);
    if (movedA || movedB) {
      line.geometry.setPositions(Array.from(cache.current));
      line.computeLineDistances();
      labelRef.current?.position.lerpVectors(_a, _b, 0.5);
      invalidate();
    }
    if (animate) line.material.dashOffset -= delta * 0.9;
  });

  return (
    <>
      <Line
        ref={lineRef}
        points={SEED_POINTS}
        color={spec.color}
        linewidth={hovered ? 4 : 2.5}
        dashed
        dashSize={0.24}
        gapSize={0.14}
        transparent
        opacity={hovered ? 1 : 0.9}
        depthTest={false}
        renderOrder={20}
        onPointerOver={(e) => { e.stopPropagation(); onHover(path.id); }}
        onPointerOut={() => onHover(null)}
      />
      <group ref={labelRef}>
        {hovered ? (
          <Html center zIndexRange={[4, 0]} style={{ pointerEvents: 'none' }} wrapperClass="kx-html">
            <div className="kx-linelabel" style={{ borderColor: spec.color }}>
              <span className="kx-linelabel__type" style={{ color: spec.color }}>{spec.label}</span>
              <span className="kx-linelabel__text">{path.label || path.id}</span>
            </div>
          </Html>
        ) : null}
      </group>
    </>
  );
}

export const TraceLines = memo(function TraceLines({ paths, partsApiRef, animate = true }) {
  const invalidate = useThree((s) => s.invalidate);
  const [hoveredId, setHoveredId] = useState(null);
  const flow = animate && !prefersReducedMotion();

  useEffect(() => { invalidate(); }, [paths, hoveredId, invalidate]);
  // Dash flow is the only continuous animation; it stops the moment the lines unmount.
  useFrame(() => { if (flow) invalidate(); });

  if (!paths?.length) return null;
  return paths.map((p) => (
    <PathLine
      key={p.id || `${p.from}-${p.to}`}
      path={p}
      partsApiRef={partsApiRef}
      animate={flow}
      hovered={hoveredId === (p.id || `${p.from}-${p.to}`)}
      onHover={(id) => setHoveredId(id === null ? null : (p.id || `${p.from}-${p.to}`))}
    />
  ));
});

export const HopTrail = memo(function HopTrail({ hops, partsApiRef }) {
  const invalidate = useThree((s) => s.invalidate);
  const lineRef = useRef(null);
  const badgeRefs = useRef([]);
  const count = hops?.length ?? 0;
  const cache = useMemo(() => new Float64Array(Math.max(count, 1) * 3).fill(Number.NaN), [count]);
  // Several hops can land on the same part; stacked badges would be unreadable,
  // so each part gets one badge listing its steps ("2 · 5").
  const badges = useMemo(() => {
    const byPart = new Map();
    (hops ?? []).forEach((h, i) => {
      const step = h.step ?? i + 1;
      if (byPart.has(h.cad_id)) byPart.get(h.cad_id).steps.push(step);
      else byPart.set(h.cad_id, { cad_id: h.cad_id, first: i, steps: [step] });
    });
    return [...byPart.values()];
  }, [hops]);

  useEffect(() => { invalidate(); }, [hops, invalidate]);

  useFrame(() => {
    const api = partsApiRef.current;
    if (!api || count === 0) return;
    let moved = false;
    const pts = [];
    for (let i = 0; i < count; i += 1) {
      if (!api.centreOf(hops[i].cad_id, _a)) continue;
      if (stash(cache, i * 3, _a)) moved = true;
      pts.push(_a.x, _a.y, _a.z);
    }
    if (moved) {
      for (let b = 0; b < badges.length; b += 1) {
        const i = badges[b].first;
        const g = badgeRefs.current[b];
        if (g && Number.isFinite(cache[i * 3])) g.position.set(cache[i * 3], cache[i * 3 + 1], cache[i * 3 + 2]);
      }
    }
    const line = lineRef.current;
    if (moved && line) {
      if (pts.length >= 6) {
        line.visible = true;
        line.geometry.setPositions(pts);
        line.computeLineDistances();
      } else {
        line.visible = false;
      }
    }
    if (moved) invalidate();
  });

  if (count === 0) return null;
  return (
    <>
      <Line
        ref={lineRef}
        points={SEED_POINTS}
        color="#e6edf7"
        linewidth={1.5}
        transparent
        opacity={0.7}
        depthTest={false}
        renderOrder={21}
      />
      {badges.map((b, i) => (
        <group key={b.cad_id} ref={(el) => { badgeRefs.current[i] = el; }}>
          <Html center zIndexRange={[4, 0]} style={{ pointerEvents: 'none' }} wrapperClass="kx-html">
            <span className={`kx-hop${b.steps.length > 1 ? ' kx-hop--multi' : ''}`} aria-hidden="true">
              {b.steps.join(' · ')}
            </span>
          </Html>
        </group>
      ))}
    </>
  );
});
